/**
 * Plain text -> runs of text and links (2026-09-26).
 *
 * Notes are stored as plain text. A URL typed into one becomes clickable at
 * RENDER time — nothing is stored differently, so every existing note with a
 * raw URL lights up too. The pattern and the trailing-punctuation rule are
 * the same as the phone's `lib/utils/linkify.dart`, so both surfaces linkify
 * exactly the same substrings. Pure; `LinkifiedText` does the rendering.
 */

// Three shapes, in priority order: a full URL, a `www.` host, or a BARE domain
// on a short allowlist of endings (2026-09-26 option A). The allowlist is the
// price of not linkifying `file.txt`, `e.g.`, `v1.2`. Keep in lockstep with
// the phone's `linkify.dart`.
const TLDS = "com|net|org|us|co|io|app|dev|edu|gov|info";
const URL_PATTERN = new RegExp(
  `(https?:\\/\\/[^\\s<>"]+|www\\.[^\\s<>"]+|\\b[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.(?:${TLDS})\\b(?:\\/[^\\s<>"]*)?)`,
  "gi"
);
// Sentence punctuation that trails a pasted link belongs to the sentence.
const TRAILING = new Set([".", ",", ";", ":", "!", "?", ")", "]", "}", "'", '"']);
const hasScheme = (s) => /^https?:\/\//i.test(s);

/** @returns {{ text: string, href: string | null }[]} */
export function splitLinks(text) {
  const s = String(text ?? "");
  const runs = [];
  let last = 0;
  URL_PATTERN.lastIndex = 0;
  let m;
  while ((m = URL_PATTERN.exec(s)) !== null) {
    let raw = m[0];
    let end = m.index + raw.length;
    // A bare-domain match glued to an `@` is the host of an e-mail address;
    // glued to a `/` it is the host of some OTHER scheme's URL (ftp://…).
    if (m.index > 0 && (s[m.index - 1] === "@" || s[m.index - 1] === "/")) continue;
    while (raw && TRAILING.has(raw[raw.length - 1])) {
      raw = raw.slice(0, -1);
      end -= 1;
    }
    if (!raw) continue;
    const href = hasScheme(raw) ? raw : `https://${raw}`;
    let ok = false;
    try {
      const u = new URL(href);
      ok = (u.protocol === "http:" || u.protocol === "https:") && !!u.hostname;
    } catch {
      ok = false;
    }
    if (!ok) continue;
    if (m.index > last) runs.push({ text: s.slice(last, m.index), href: null });
    runs.push({ text: raw, href });
    last = end;
  }
  if (last < s.length) runs.push({ text: s.slice(last), href: null });
  return runs;
}
